/**
 * /library — the Library: your tracks, with search, genre, sort and favourites.
 *
 * CLICKING A TRACK NO LONGER OPENS THE STUDIO. It used to navigate straight to
 * /library/:songId — the Studio workspace — so there was no way to look at one of your own
 * songs without being put into an editing environment, and the gesture people use to
 * browse a library was the same gesture that starts editing. A click now opens the song's
 * detail view over the page, which is exactly what a click on a song does on the Home page;
 * the Studio is reached deliberately, through an "Open in Studio" button in that view or the
 * matching item in a row's More menu.
 *
 * THE DETAIL VIEW IS THE CATALOGUE'S DIALOG IN ITS 'library' FORM (Batch 6 §9): the cover,
 * the title and metadata, the full lyric sheet, Play, Share and Open in Studio — with the
 * Love and Save buttons deliberately absent, because §9 asks for them to go from this view.
 * Both remain on the row itself, which is where the Library has always kept them.
 *
 * The workspace itself is unchanged and still lives at /library/:songId. This route used
 * to be its parent and rendered the Studio drawer through an <Outlet />; the workspace
 * redesign made the Studio too large to sit in a panel over the grid, so it replaces this
 * page instead of covering it. Back returns here, as closing the drawer did.
 *
 * Redesigned in the Studio pass. The filter row used to be four controls of four
 * different shapes competing with the heading for attention; the search field is
 * now the only one always drawn at full width, and the three refinements sit beside
 * it as a quieter group. The songs themselves became rows — see songs/SongCard.
 *
 * The <h1> used to read "Lyrixa Studio", because the sidebar's "Lyrixa Studio" row
 * pointed here — the heading matched the thing the user had clicked. That row now
 * points at the Studio itself and the Library has its own, so the heading says what
 * the page is. The two views underneath it are Main Library and Playlists.
 */
import type { CSSProperties } from 'react';
import { Music2, Search, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Btn, LoadError, LoadSkeleton } from '../components/ui';
import { LibraryTabs } from '../components/playlists';
import { SongGrid } from '../components/songs';
import { StudioHeader } from '../components/studio';
import { UNKNOWN_API_ERROR_MESSAGE } from '../api/errors';
import { GENRES } from '../data/music';
import { usePlayer, useSongs } from '../hooks';
import { isFirstLoad } from '../stores/loadState';
import { useSongsStore } from '../stores/songsStore';
import { useUiStore } from '../stores/uiStore';
import type { SongSortOrder } from '../types/domain';
import { c } from '../styles/theme';
import { paths } from './paths';

/** Shared look for the two selects, so the refinement group reads as one control. */
const SELECT: CSSProperties = {
  fontSize: 12,
  height: 32,
  background: c.surface1,
  border: `1px solid ${c.border}`,
  borderRadius: 8,
  color: c.text2,
  padding: '0 8px',
  cursor: 'pointer',
};

export default function Library() {
  const navigate = useNavigate();
  const { songs, filteredSongs, filters, setFilters, toggleFavorite, duplicateSong, downloadSong } =
    useSongs();
  /*
    `playInMiniPlayer` alongside `toggle`, and the pair is the point: the row's accent circle
    plays the song where you are (`toggle`) and its menu item plays it in the floating bar
    (`playInMiniPlayer`). Both are the same store and the same clock — the second one also
    opens the bar. Neither is a second player; see hooks/usePlayer.
  */
  const { songId: playingId, paused, elapsed, speed, loading, toggle, seek, playInMiniPlayer } = usePlayer();
  const setEditTarget = useUiStore((s) => s.setEditTarget);
  const setDeleteTarget = useUiStore((s) => s.setDeleteTarget);
  const setAddToPlaylistTarget = useUiStore((s) => s.setAddToPlaylistTarget);
  /*
    What a row's click raises now that it no longer navigates: the song detail view, through
    the same uiStore target and the same AppModals dialog the Home page's cards use. One
    dialog, one target — this route adds an entry point to it rather than a second preview.

    RAISED WITH 'library', which is Batch 6 §9: the Library's detail view drops the Love and
    Save buttons and gains an explicit "Open in Studio". The Home page's identical call passes
    nothing and keeps all four discovery actions — see uiStore.setLyricsTarget's default and
    types/domain.LyricsSurface. Note that this row still has its own favourite control and its
    own "Add to a playlist" menu item, so §9 removes those two buttons from the DIALOG and not
    from the Library.
  */
  const setLyricsTarget = useUiStore((s) => s.setLyricsTarget);
  // The listing decisions are confirmations, so the row raises a target and AppModals owns
  // the dialog — the same arrangement as rename, delete and add-to-playlist above. Nothing
  // here calls setVisibility: this route asks the question, the dialog's confirm answers it.
  const setMakeDiscoverableTarget = useUiStore((s) => s.setMakeDiscoverableTarget);
  const setRemoveFromCatalogueTarget = useUiStore((s) => s.setRemoveFromCatalogueTarget);
  /*
    THE READ STATE, from the store rather than through useSongs.

    Read here for the same reason the six uiStore selectors above are: one field per
    selector call (the Zustand v5 rule this codebase follows — an object literal is a new
    reference under Object.is on every render), and no hook needs widening to carry it.
    `useSongs` coordinates songs with credits and entitlements; `load` is one store's own
    fact and every screen that shows a collection reads it the same way.
  */
  const load = useSongsStore((s) => s.load);
  const loadSongs = useSongsStore((s) => s.loadSongs);

  return (
    <div>
      <StudioHeader title="Library" subtitle="Your tracks and playlists." />

      <LibraryTabs active="tracks" />

      {/* The filter row is hidden on an empty library: there is nothing to filter,
          and four idle controls above an empty state is the sort of thing that made
          the original screen feel like an admin panel. */}
      {songs.length > 0 && (
        <div className="lyx-library-filters">
          <div
            className="lyx-library-search"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              height: 32,
              border: `1px solid ${c.border}`,
              borderRadius: 8,
              padding: '0 10px',
              background: c.surface1,
            }}
          >
            <Search size={13} color={c.text3} style={{ flexShrink: 0 }} />
            <input
              value={filters.search}
              onChange={(e) => setFilters({ search: e.target.value })}
              placeholder="Search your tracks"
              aria-label="Search songs"
              style={{
                border: 'none',
                background: 'transparent',
                color: c.text1,
                fontSize: 13,
                width: '100%',
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <select
              value={filters.genre}
              onChange={(e) => setFilters({ genre: e.target.value })}
              aria-label="Filter by genre"
              style={SELECT}
            >
              <option>All genres</option>
              {GENRES.map((g) => (
                <option key={g}>{g}</option>
              ))}
            </select>
            <select
              value={filters.sortOrder}
              onChange={(e) => setFilters({ sortOrder: e.target.value as SongSortOrder })}
              aria-label="Sort order"
              style={SELECT}
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
            </select>
            <button
              type="button"
              onClick={() => setFilters({ favoritesOnly: !filters.favoritesOnly })}
              aria-pressed={filters.favoritesOnly}
              aria-label="Favorites"
              title="Favorites"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: 8,
                cursor: 'pointer',
                flexShrink: 0,
                border: filters.favoritesOnly
                  ? `1px solid ${c.accentBorder}`
                  : `1px solid ${c.border}`,
                background: filters.favoritesOnly ? c.accentSoft : c.surface1,
              }}
            >
              {/* The glyph is the whole of this control — there is no label beside
                  it — so it is a meaningful icon, which theme.ts puts at text2. text3
                  does not clear even the 3:1 non-text threshold on two surfaces. */}
              <Star
                size={13}
                color={filters.favoritesOnly ? c.accent : c.text2}
                fill={filters.favoritesOnly ? c.accent : 'none'}
              />
            </button>
          </div>
        </div>
      )}

      {/* ---- the four read states, in the order they can occur ----
          WAS `songs.length === 0 ? <empty> : …`, WHICH IS THREE STATES COLLAPSED INTO ONE.
          An empty array meant "you have no tracks" whether the fetch had not started, was
          in flight, or had failed — so a 500 from GET /songs rendered "No tracks yet" and
          invited the user to create a song they may already have. See stores/loadState.ts,
          which exists for exactly this.

          The two predicates carry `hasData` for a reason: a refresh over a populated
          library must not blank it, and an empty list is only a real empty state once the
          server has actually answered. */}
      {isFirstLoad(load, songs.length > 0) ? (
        <LoadSkeleton rows={4} label="Loading your songs…" />
      ) : load.status === 'error' && songs.length === 0 ? (
        /*
          THE FAILURE BRANCH, and the reason it is ahead of the empty one. Only shown when
          there is nothing to show behind it: a failed REFRESH leaves the library on screen
          (loadState.ts: "the local data is untouched"), and replacing songs the user can
          see with an error box would be a worse answer than keeping them.

          No fixture, no "cached" claim, no retry-until-it-works loop. The message is the
          server's own, through the one error contract.
        */
        <LoadError message={load.error?.message ?? UNKNOWN_API_ERROR_MESSAGE} onRetry={loadSongs} />
      ) : songs.length === 0 ? (
        // Small icon, one line, one action — no illustration and no dashed box.
        <div style={{ padding: '40px 0 20px', textAlign: 'center' }}>
          <div
            style={{
              width: 40,
              height: 40,
              margin: '0 auto 14px',
              borderRadius: 10,
              background: c.surface2,
              border: `1px solid ${c.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Music2 size={17} color={c.text3} />
          </div>
          <p
            style={{
              fontFamily: 'Space Grotesk',
              fontWeight: 600,
              fontSize: 14,
              margin: '0 0 4px',
            }}
          >
            No tracks yet
          </p>
          <p style={{ fontSize: 12, color: c.text2, margin: '0 0 18px' }}>
            Create your first song and it will appear here.
          </p>
          <Btn onClick={() => navigate(paths.create)}>
            <Music2 size={14} />
            Create music
          </Btn>
        </div>
      ) : filteredSongs.length === 0 ? (
        // The only thing on screen when a filter matches nothing, so text2: if this
        // is unreadable the screen looks broken rather than empty.
        <p style={{ fontSize: 12, color: c.text2, padding: '24px 0', textAlign: 'center' }}>
          No songs match your search or filters.
        </p>
      ) : (
        <SongGrid
          songs={filteredSongs}
          playingId={playingId}
          paused={paused}
          elapsed={elapsed}
          speed={speed}
          onSelect={(song) => setLyricsTarget(song, 'library')}
          // The explicit route into the workspace from the ROW's menu. The detail view the
          // click above opens carries the same action as a button (§9) and sends it to the
          // same route — see AppModals. Same destination the whole-row click used to take,
          // a deliberate press away either way.
          onOpenInStudio={(song) => navigate(paths.song(song.id))}
          onTogglePlay={toggle}
          onSeek={seek}
          loading={Boolean(playingId && loading)}
          // The row's explicit request for the floating bar. Playing a song from this screen
          // deliberately does NOT raise it — the row itself is the player here — so this is
          // how someone who wants the transport to follow them asks for it.
          onPlayInMiniPlayer={(song) => playInMiniPlayer(song.id)}
          onToggleFavorite={toggleFavorite}
          onEdit={setEditTarget}
          onDuplicate={duplicateSong}
          onDownload={downloadSong}
          onAddToPlaylist={setAddToPlaylistTarget}
          onMakeDiscoverable={setMakeDiscoverableTarget}
          onRemoveFromCatalogue={setRemoveFromCatalogueTarget}
          onDelete={setDeleteTarget}
        />
      )}
    </div>
  );
}
